import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  CheckCircle, XCircle, Zap, Rocket, TrendingUp, Star, Building2,
  ArrowUpRight, Loader2, Users, Calculator, Target
} from 'lucide-react';
import { getPlanColor, getLiveEligibilityBadge, isCustomPlan } from './billingHelpers';

const getPlanIconEl = (planId) => {
  switch (planId) {
    case 'free': return <Zap className="h-6 w-6" />;
    case 'starter': return <Rocket className="h-6 w-6" />;
    case 'growth': return <TrendingUp className="h-6 w-6" />;
    case 'scale': return <Star className="h-6 w-6" />;
    case 'enterprise': return <Building2 className="h-6 w-6" />;
    default: return <Star className="h-6 w-6" />;
  }
};

// ROI / reach calculator — translates "leads I want to contact" into the plan
// that covers it, so buyers stop fearing credits and see outcomes.
const CREDITS_PER_LEAD = 12; // ~$0.12 mixed cadence (score + 3 emails + 1 reveal)
const RoiCalculator = ({ plans, currency }) => {
  const [leads, setLeads] = useState(200);
  const isINR = currency === 'INR';
  const money = (v) => (v == null ? '—' : isINR ? `₹${Number(v).toLocaleString('en-IN')}` : `$${v}`);
  const needed = leads * CREDITS_PER_LEAD;
  const payable = plans
    .filter((p) => p.plan_id !== 'free' && !isCustomPlan(p) && (p.included_credits || 0) > 0)
    .sort((a, b) => (a.included_credits || 0) - (b.included_credits || 0));
  const rec = payable.find((p) => (p.included_credits || 0) >= needed) || null;
  const recPrice = rec ? (isINR ? rec.monthly_price_inr : rec.monthly_price_usd) : null;
  const costPerLead = rec && recPrice && leads > 0
    ? (isINR ? `₹${(recPrice / leads).toFixed(0)}` : `$${(recPrice / leads).toFixed(2)}`)
    : null;

  return (
    <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] to-transparent" data-testid="roi-calculator">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="h-5 w-5 text-amber-500" />Find your ROI
        </CardTitle>
        <CardDescription>How many leads do you want your AI to work each month?</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm text-muted-foreground">Leads / month</span>
              <span className="text-2xl font-extrabold text-amber-500" data-testid="roi-leads-value">{leads.toLocaleString()}</span>
            </div>
            <input
              type="range" min="20" max="3000" step="20" value={leads}
              onChange={(e) => setLeads(Number(e.target.value))}
              className="w-full accent-amber-500 cursor-pointer"
              data-testid="roi-leads-slider"
            />
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
              <span>20</span><span>3,000</span>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4" data-testid="roi-result">
            <p className="text-xs text-muted-foreground">Recommended plan</p>
            {rec ? (
              <>
                <p className="text-xl font-bold mt-0.5">{rec.name} <span className="text-base font-normal text-muted-foreground">· {money(recPrice)}/mo</span></p>
                <div className="flex gap-4 mt-2 text-sm">
                  <div><span className="text-muted-foreground">Included credits:</span> <span className="font-semibold">{rec.included_credits?.toLocaleString()}</span></div>
                  <div><span className="text-muted-foreground">Cost / lead:</span> <span className="font-semibold text-emerald-500">{costPerLead}</span></div>
                </div>
              </>
            ) : (
              <>
                <p className="text-xl font-bold mt-0.5">Enterprise</p>
                <p className="text-sm text-muted-foreground mt-1">Beyond self-serve volume — talk to sales for custom credits.</p>
              </>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">Estimate based on a typical mixed cadence (lead score + 3 emails + 1 reveal). Voice/WhatsApp add usage.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const BillingPlansTab = ({
  plans, currentPlan, billing, checkoutLoading, changingPlan, portalLoading,
  onStripeCheckout, onPlanChange, onOpenPortal, onRazorpayUpgrade,
  gateway = 'stripe', currency = 'USD', gstRatePct = 18, reverseTrialDays = 0
}) => {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [planSegment, setPlanSegment] = useState('smb');
  const isINR = currency === 'INR';
  const money = (v) => (isINR ? `₹${Number(v).toLocaleString('en-IN')}` : `$${v}`);

  // Arrange cards low → high price (Free → Manual Pro → Growth → Scale), custom/enterprise last.
  const orderedPlans = [...(plans || [])].sort((a, b) => {
    const pa = isCustomPlan(a) ? Number.MAX_SAFE_INTEGER : (a.monthly_price_usd || 0);
    const pb = isCustomPlan(b) ? Number.MAX_SAFE_INTEGER : (b.monthly_price_usd || 0);
    return pa - pb;
  });
  const visibleCards = orderedPlans.filter(plan => plan.segment === planSegment || (!plan.segment && planSegment === 'smb'));
  const gridColsClass = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-2 lg:grid-cols-4' }[visibleCards.length] || 'md:grid-cols-3';

  return (
    <div className="space-y-6">
      {/* Segment Toggle + Billing Interval */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="inline-flex rounded-lg border border-border p-1 bg-muted/30" data-testid="plan-segment-toggle">
          <button
            onClick={() => setPlanSegment('smb')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              planSegment === 'smb' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="segment-smb-btn"
          >
            <Users className="h-4 w-4 inline mr-2" />SMB & Startups
          </button>
          <button
            onClick={() => setPlanSegment('enterprise')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              planSegment === 'enterprise' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="segment-enterprise-btn"
          >
            <Building2 className="h-4 w-4 inline mr-2" />Enterprise
          </button>
        </div>

        <div className="flex items-center gap-3" data-testid="billing-interval-toggle">
          <span className={`text-sm font-medium ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
          <Switch
            checked={billingInterval === 'annual'}
            onCheckedChange={(checked) => setBillingInterval(checked ? 'annual' : 'monthly')}
          />
          <span className={`text-sm font-medium ${billingInterval === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Annual
            <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Save 20%</Badge>
          </span>
        </div>
      </div>

      {/* Plan Cards Grid */}
      <div className={`grid gap-5 grid-cols-1 ${gridColsClass}`}>
        {visibleCards
          .map((plan) => {
          const isCurrentPlan = currentPlan?.plan_id === plan.plan_id;
          const isPopular = plan.plan_id === 'growth' || plan.popular;
          const isEnterprise = isCustomPlan(plan);
          const isFree = plan.plan_id === 'free';
          const displayPrice = isINR
            ? (billingInterval === 'annual' && plan.annual_price_inr ? plan.annual_price_inr : plan.monthly_price_inr)
            : (billingInterval === 'annual' && plan.annual_price_usd ? plan.annual_price_usd : plan.monthly_price_usd);
          const baseMonthlyPrice = isINR ? plan.monthly_price_inr : plan.monthly_price_usd;
          const annualPrice = isINR ? plan.annual_price_inr : plan.annual_price_usd;
          
          return (
            <Card 
              key={plan.plan_id}
              className={`relative flex flex-col overflow-hidden transition-all duration-300 ${
                isCurrentPlan ? 'border-primary ring-2 ring-primary/20 shadow-lg shadow-primary/10' 
                  : isPopular ? 'border-amber-500/50 shadow-lg shadow-amber-500/5 hover:shadow-xl hover:border-amber-500'
                  : 'border-border hover:border-primary/50 hover:shadow-lg'
              }`}
              data-testid={`plan-card-${plan.plan_id}`}
            >
              {isPopular && !isCurrentPlan && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-1.5 text-xs font-bold tracking-wider">MOST POPULAR</div>
              )}
              {isCurrentPlan && (
                <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center py-1.5 text-xs font-bold tracking-wider">CURRENT PLAN</div>
              )}
              
              <CardHeader className={`text-center pb-2 ${(isPopular || isCurrentPlan) ? 'pt-10' : 'pt-6'}`}>
                <div className={`mx-auto p-3 rounded-2xl mb-3 ${
                  isFree ? 'bg-slate-500/10' : plan.plan_id === 'starter' ? 'bg-blue-500/10' : plan.plan_id === 'growth' ? 'bg-amber-500/10' : plan.plan_id === 'scale' ? 'bg-emerald-500/10' : 'bg-purple-500/10'
                }`}>
                  <div className={getPlanColor(plan.plan_id)}>{getPlanIconEl(plan.plan_id)}</div>
                </div>
                
                <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
                
                <div className="mt-4">
                  {displayPrice !== null && displayPrice !== undefined ? (
                    <div>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-extrabold">{money(displayPrice)}</span>
                        <span className="text-muted-foreground text-sm">/mo</span>
                      </div>
                      {billingInterval === 'annual' && baseMonthlyPrice > 0 && (
                        <p className="text-xs text-muted-foreground mt-1 line-through">{money(baseMonthlyPrice)}/mo</p>
                      )}
                      {billingInterval === 'monthly' && annualPrice && annualPrice < baseMonthlyPrice && (
                        <p className="text-xs text-emerald-500 mt-1">{money(annualPrice)}/mo billed annually</p>
                      )}
                      {isINR && !isFree && (
                        <p className="text-[11px] text-muted-foreground mt-1">+{gstRatePct}% GST</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-lg font-semibold text-purple-400">Custom Pricing</div>
                  )}
                </div>
                
                <div className="mt-3">{getLiveEligibilityBadge(plan, Badge)}</div>

                {plan.outcome_framing && (
                  <div className="mt-3 mx-auto inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1" data-testid={`plan-outcome-${plan.plan_id}`}>
                    <Target className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold text-primary">{plan.outcome_framing}</span>
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="flex-grow pt-0 pb-2">
                <div className="border-t border-border/30 pt-4">
                  <ul className="space-y-2.5">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <CheckCircle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                          isFree ? 'text-slate-500' : plan.plan_id === 'starter' ? 'text-blue-500' : plan.plan_id === 'growth' ? 'text-amber-500' : plan.plan_id === 'scale' ? 'text-emerald-500' : 'text-purple-500'
                        }`} />
                        <span className="text-sm leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-border/30">
                  <div className="text-center p-2 rounded bg-muted/30">
                    <div className="text-xs text-muted-foreground">AI Credits</div>
                    <div className="text-sm font-bold">{plan.included_credits?.toLocaleString()}/mo</div>
                  </div>
                  <div className="text-center p-2 rounded bg-muted/30">
                    <div className="text-xs text-muted-foreground">Users</div>
                    <div className="text-sm font-bold">{plan.max_users >= 999 ? 'Unlimited' : `Up to ${plan.max_users}`}</div>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="pt-2 pb-6 mt-auto">
                {isEnterprise ? (
                  <Button className="w-full h-12 text-base font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    onClick={() => window.location.href = 'mailto:info@lockene.ai?subject=Enterprise Plan Inquiry'}
                    data-testid="contact-sales-btn"
                  >
                    <Building2 className="h-5 w-5 mr-2" />Contact Sales
                  </Button>
                ) : isCurrentPlan ? (
                  <Button className="w-full h-12 text-base font-semibold" variant="secondary" disabled>
                    <CheckCircle className="h-5 w-5 mr-2" />Current Plan
                  </Button>
                ) : isFree ? (
                  <Button className="w-full h-12 text-base font-semibold" variant="outline"
                    onClick={() => onPlanChange('free')} disabled={changingPlan} data-testid={`select-plan-${plan.plan_id}`}
                  >
                    {changingPlan ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Processing...</> : 'Downgrade to Free'}
                  </Button>
                ) : (gateway === 'razorpay' && billing?.razorpay_subscription_id && billing?.billing_status === 'active') ? (
                  <Button className={`w-full h-12 text-base font-semibold ${isPopular ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' : ''}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRazorpayUpgrade(plan.plan_id, billingInterval); }}
                    disabled={checkoutLoading !== null} data-testid={`upgrade-plan-${plan.plan_id}`}
                  >
                    {checkoutLoading === plan.plan_id ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Starting…</> : <><ArrowUpRight className="h-5 w-5 mr-2" />{(displayPrice || 0) > (currentPlan?.monthly_price_usd || 0) ? 'Upgrade plan' : 'Switch plan'}</>}
                  </Button>
                ) : (billing?.stripe_subscription_id && billing?.billing_status === 'active') ? (
                  <Button className={`w-full h-12 text-base font-semibold ${isPopular ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' : ''}`}
                    onClick={() => onOpenPortal()} disabled={portalLoading} data-testid={`manage-plan-${plan.plan_id}`}
                  >
                    {portalLoading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Opening Portal...</> : <><ArrowUpRight className="h-5 w-5 mr-2" />{(displayPrice || 0) > (currentPlan?.monthly_price_usd || 0) ? 'Upgrade via Portal' : 'Change via Portal'}</>}
                  </Button>
                ) : (
                  <div className="w-full">
                  <Button className={`w-full h-12 text-base font-semibold ${isPopular ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' : ''}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onStripeCheckout(plan.plan_id, e, billingInterval); }}
                    disabled={checkoutLoading !== null} data-testid={`select-plan-${plan.plan_id}`}
                  >
                    {checkoutLoading === plan.plan_id ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Redirecting...</> : <><Zap className="h-5 w-5 mr-2" />{(reverseTrialDays > 0 && !billing?.trial_used) ? `Start ${reverseTrialDays}-day free trial` : 'Subscribe'}</>}
                  </Button>
                  {(reverseTrialDays > 0 && !billing?.trial_used) && (
                    <p className="text-[11px] text-center text-muted-foreground mt-2" data-testid={`trial-note-${plan.plan_id}`}>
                      Card required · then {money(displayPrice)}/mo · cancel anytime
                    </p>
                  )}
                  </div>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* ROI / reach calculator */}
      {planSegment === 'smb' && <RoiCalculator plans={plans} currency={currency} />}

      {/* Feature Comparison */}
      <Card className="mt-8" data-testid="feature-comparison-card">
        <CardHeader>
          <CardTitle className="text-lg">Feature Comparison</CardTitle>
          <CardDescription>See what&apos;s included in each plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 pr-4 font-medium text-muted-foreground">Feature</th>
                  {orderedPlans.filter(p => p.segment === planSegment || (!p.segment && planSegment === 'smb')).map(plan => (
                    <th key={plan.plan_id} className={`text-center py-3 px-3 font-semibold ${getPlanColor(plan.plan_id)}`}>{plan.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'AI Brain Mode', values: { manual_pro: 'None', free: 'Simulated', starter: 'Supervised', growth: 'Autonomous', scale: 'Autonomous', enterprise: 'Custom' }},
                  { name: 'AI Credits/month', values: { manual_pro: '0', free: '$5 free', starter: '500', growth: '2,000', scale: '15,000', enterprise: '50,000+' }},
                  { name: '≈ Leads fully worked/mo', highlight: true, values: { manual_pro: false, free: '~40', starter: '~170', growth: '~170', scale: '~1,250', enterprise: 'Unlimited' }},
                  { name: '≈ AI emails/mo', highlight: true, values: { manual_pro: false, free: '~2,500', starter: '~10k', growth: '~10,000', scale: '~75,000', enterprise: 'Unlimited' }},
                  { name: '≈ Voice AI calls/mo', highlight: true, values: { manual_pro: false, free: false, starter: false, growth: '~40', scale: '~300', enterprise: 'Unlimited' }},
                  { name: 'Contact Reveals', values: { manual_pro: '0', free: '10', starter: '200', growth: '500', scale: '2,000', enterprise: '10,000+' }},
                  { name: 'Leads', values: { manual_pro: 'Up to 5,000', free: '500', starter: '2,000', growth: '10,000', scale: '50,000', enterprise: 'Unlimited' }},
                  { name: 'User Seats', values: { manual_pro: '15', free: '1', starter: '3', growth: '10', scale: '50', enterprise: 'Unlimited' }},
                  { name: 'CSV Import / Export', values: { manual_pro: true, free: true, starter: true, growth: true, scale: true, enterprise: true }},
                  { name: 'Manual Outreach & Cadences', values: { manual_pro: true, free: true, starter: true, growth: true, scale: true, enterprise: true }},
                  { name: 'Email Outreach', values: { manual_pro: 'Manual', free: false, starter: true, growth: true, scale: true, enterprise: true }},
                  { name: 'Voice AI Calls', values: { manual_pro: false, free: false, starter: false, growth: true, scale: true, enterprise: true }},
                  { name: 'WhatsApp Automation *', values: { manual_pro: false, free: false, starter: false, growth: true, scale: true, enterprise: true }},
                  { name: 'LinkedIn Extension', values: { manual_pro: false, free: false, starter: false, growth: true, scale: true, enterprise: true }},
                  { name: 'Multi-Language Voice', values: { manual_pro: false, free: false, starter: false, growth: true, scale: true, enterprise: true }},
                  { name: 'Workflow Builder', values: { manual_pro: false, free: false, starter: 'Basic', growth: true, scale: true, enterprise: true }},
                  { name: 'CRM Sync', values: { manual_pro: false, free: false, starter: false, growth: false, scale: true, enterprise: true }},
                  { name: 'SSO / SAML', values: { manual_pro: false, free: false, starter: false, growth: false, scale: true, enterprise: true }},
                  { name: 'API Access', values: { manual_pro: false, free: false, starter: false, growth: false, scale: true, enterprise: true }},
                  { name: 'LIVE Mode', values: { manual_pro: false, free: false, starter: true, growth: true, scale: true, enterprise: true }},
                  { name: 'Dedicated Support', values: { manual_pro: false, free: false, starter: false, growth: false, scale: true, enterprise: '24/7' }},
                ].map((row) => (
                  <tr key={row.name} className={`border-b border-border/30 ${row.highlight ? 'bg-amber-500/[0.04]' : ''}`}>
                    <td className={`py-2.5 pr-4 ${row.highlight ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`}>{row.name}</td>
                    {orderedPlans.filter(p => p.segment === planSegment || (!p.segment && planSegment === 'smb')).map(plan => {
                      const val = row.values[plan.plan_id];
                      return (
                        <td key={plan.plan_id} className="text-center py-2.5 px-3">
                          {val === true ? <CheckCircle className="h-4 w-4 mx-auto text-emerald-500" /> : val === false ? <XCircle className="h-4 w-4 mx-auto text-slate-600" /> : <span className="text-sm font-medium">{val}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            * <span className="font-medium text-foreground">WhatsApp messages</span> are billed per delivered template from your AI credits at
            Meta&apos;s rate + a 30% platform margin. User-initiated replies within the 24-hour window are free.
            Reach estimates (≈) assume a typical mixed cadence and vary with your actual usage.
          </p>
        </CardContent>
      </Card>
      
      <div className="text-center p-4 bg-muted/30 rounded-lg border border-border/30">
        <p className="text-sm text-muted-foreground">
          All plans include core CRM modules. AI features and channels scale with your plan.
          <br />
          Need help choosing? <a href="mailto:sales@lockene.ai" className="text-primary hover:underline font-medium">Talk to our sales team</a>
        </p>
      </div>
    </div>
  );
};
